import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ErrorDisplay } from '@/components/common/error-display'
import { ChangeManagerButton, EditUserRolesButton } from '@/features/users'
import { useDirectReportProfile } from '@/features/users/services/users.convex'
import { RemoveUserButton } from '@/features/users/components/RemoveUserButton'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute(
  '/(dashboard)/direct-reports/$directReportId/user-settings/',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      name: (search.name as string) || undefined,
    }
  },
  beforeLoad: ({ params, search }) => ({
    breadcrumbs: [
      { title: 'Direct Reports', href: '/direct-reports' },
      { title: search.name || params.directReportId },
      { title: 'User Settings' },
    ],
  }),
})

function RouteComponent() {
  const { directReportId } = Route.useParams()
  const { data, isLoading, error } = useDirectReportProfile(directReportId)

  function onEditRoles() {
    toast.success(`${data?.name}'s roles have been successfully updated!`)
  }

  function onChangeManager() {
    toast.success(`${data?.name}'s manager has been successfully changed!`)
  }

  function onDelete() {
    toast.success(
      `${data?.name} has been successfully removed from the organization!`,
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6 mt-2">
        {/* User Roles Section Skeleton */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
          <div className="space-y-2 flex-1">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-9 w-28" />
        </div>

        {/* Change Manager Section Skeleton */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
          <div className="space-y-2 flex-1">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-56" />
          </div>
          <Skeleton className="h-9 w-36" />
        </div>

        {/* Delete User Section Skeleton */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
          <div className="space-y-2 flex-1">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
    )
  }

  if (error) {
    return <ErrorDisplay error={error} title="Error loading user" />
  }

  return (
    <div className="space-y-6 mt-2">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
        <div>
          <h2 className="text-xl font-semibold text-foreground">User Roles</h2>
          <p className="text-sm text-muted-foreground">
            Manage roles and permissions for {data?.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <EditUserRolesButton
            userId={data?.id ?? directReportId}
            userName={data?.name ?? ''}
            onSuccess={onEditRoles}
          />
        </div>
      </div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Change Manager
          </h2>
          <p className="text-sm text-muted-foreground">
            Change the manager for {data?.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ChangeManagerButton
            userId={data?.id ?? directReportId}
            userName={data?.name ?? ''}
            currentManagerId={data?.manager?.id}
            onSuccess={onChangeManager}
          />
        </div>
      </div>

      <div className="p-6 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 flex items-center justify-between gap-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Remove User</h2>
          <p className="text-sm text-muted-foreground">
            Remove {data?.name} from the organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RemoveUserButton
            userId={data?.id ?? directReportId}
            userName={data?.name ?? ''}
            onSuccess={onDelete}
          />
        </div>
      </div>
    </div>
  )
}
